import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InboxItem, KIND_COLOR, KIND_LABEL, STATUS_LABEL, ConversationKind, ParticipantRole } from './types';
import { formatDateTime } from '../../utils/datetime';
import ComposeMessageModal from '../../components/ComposeMessageModal';
import { useAppStore } from '../../App';

const ROLE_LABEL: Partial<Record<ParticipantRole, string>> = {
 sender: '내가 보냄',
 recipient: '받음',
 observer: '관찰',
 cc: '참조',
};

const ROLE_TONE: Partial<Record<ParticipantRole, string>> = {
 sender: 'bg-blue-50 text-blue-700',
 recipient: 'bg-green-50 text-green-700',
 observer: 'bg-gray-50 text-gray-600',
 cc: 'bg-gray-50 text-gray-600',
};

const Inbox: React.FC = () => {
 const navigate = useNavigate();
 const [kindFilter, setKindFilter] = useState<ConversationKind | ''>('');
 const [archivedView, setArchivedView] = useState(false);
 const [items, setItems] = useState<InboxItem[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showCompose, setShowCompose] = useState(false);
 const [markingAll, setMarkingAll] = useState(false);
 const refreshUnreadInbox = useAppStore(s => s.refreshUnreadInbox);

 const loadList = useCallback(async () => {
  setLoading(true);
  setError(null);
  const params = new URLSearchParams();
  if (kindFilter) params.set('kind', kindFilter);
  if (archivedView) params.set('archived', 'true');
  try {
   const r = await fetch(`/messages/list?${params.toString()}`, { credentials: 'include' });
   const j = await r.json();
   if (!j.success) { setError(j.error || '조회 실패'); setItems([]); return; }
   setItems(j.data?.items ?? []);
  } catch (e: any) {
   setError(e.message || '조회 실패');
  } finally {
   setLoading(false);
  }
 }, [kindFilter, archivedView]);

 useEffect(() => { void loadList(); }, [loadList]);

 const hasUnread = items.some(x => x.unread);
 const onMarkAllRead = async () => {
  setMarkingAll(true);
  try {
   const r = await fetch('/messages/read-all', { method: 'PATCH', credentials: 'include' });
   const j = await r.json();
   if (!j.success) { alert(j.error || '읽음 처리 실패'); return; }
   await loadList();
   void refreshUnreadInbox();
  } catch (e: any) {
   alert(e.message || '읽음 처리 실패');
  } finally {
   setMarkingAll(false);
  }
 };

 const onToggleArchive = async (cid: string, archived: boolean) => {
  try {
   const r = await fetch(`/messages/${cid}/archive`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived }),
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || (archived ? '보관 실패' : '복원 실패')); return; }
   setItems(prev => prev.filter(x => x.id !== cid));
  } catch (e: any) {
   alert(e.message || (archived ? '보관 실패' : '복원 실패'));
  }
 };

 const selectKind = (k: ConversationKind | '') => {
  setKindFilter(k);
  setArchivedView(false);
 };

 const selectArchived = () => {
  setArchivedView(true);
  setKindFilter('');
 };

 return (
  <div>
   <div className="flex items-start justify-between gap-2 mb-2">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">메시지</h1>
    <div className="flex gap-2">
     <button
      type="button"
      onClick={onMarkAllRead}
      disabled={markingAll || archivedView || !hasUnread}
      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
     >{markingAll ? '처리 중…' : '모두 읽음'}</button>
     <button
      type="button"
      onClick={() => setShowCompose(true)}
      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm whitespace-nowrap"
     >+ 새 메시지</button>
    </div>
   </div>
   <p className="text-sm text-gray-500 leading-relaxed mb-4">
    일반 메시지·마켓 제안·가입 초대를 한 곳에서 봅니다. 협상은 받은/보낸이 한 thread 안에서 진행됩니다.
    끝난 항목은 우측 '보관' 버튼으로 정리하고, 보관함 탭에서 다시 볼 수 있습니다.
   </p>

   <div className="mb-3 flex gap-2 text-sm flex-wrap">
    <button
     onClick={() => selectKind('')}
     className={`px-2 py-1 ${!kindFilter && !archivedView ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
    >전체</button>
    {(Object.keys(KIND_LABEL) as ConversationKind[]).map(k => (
     <button
      key={k}
      onClick={() => selectKind(k)}
      className={`px-2 py-1 ${kindFilter === k && !archivedView ? KIND_COLOR[k] : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
     >
      {KIND_LABEL[k]}
     </button>
    ))}
    <button
     onClick={selectArchived}
     className={`px-2 py-1 ${archivedView ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
    >보관함</button>
   </div>

   {error && <div className="bg-red-50 text-red-700 p-2 text-sm mb-3">{error}</div>}
   {loading && <div className="text-gray-500 py-8 text-center text-sm">로딩 중...</div>}
   {!loading && items.length === 0 && (
    <div className="text-gray-400 py-12 text-center text-sm">
     {archivedView ? '보관함이 비어 있습니다.' : '아직 메시지가 없습니다.'}
    </div>
   )}

   <ul className="divide-y divide-gray-200">
    {items.map(it => (
     <li
      key={it.id}
      onClick={() => navigate(`/inbox/${it.id}`)}
      className={`py-2 px-2 cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${it.unread ? 'font-medium' : ''}`}
     >
      <span className={`text-[10px] px-1.5 py-0.5 ${KIND_COLOR[it.kind]}`}>{KIND_LABEL[it.kind]}</span>
      <span className={`text-[10px] px-1.5 py-0.5 ${ROLE_TONE[it.my_role] ?? 'bg-gray-50 text-gray-500'}`}>
       {ROLE_LABEL[it.my_role] ?? it.my_role}
      </span>
      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600">
       {STATUS_LABEL[it.status] ?? it.status}
      </span>
      <span className="flex-1 truncate text-sm">{it.subject ?? '(제목 없음)'}</span>
      {it.unread && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5">new</span>}
      <span className="text-xs text-gray-400 whitespace-nowrap">
       {formatDateTime(it.last_message_at)}
      </span>
      <button
       onClick={(e) => { e.stopPropagation(); onToggleArchive(it.id, !archivedView); }}
       className="text-[10px] text-gray-400 hover:text-gray-700 px-2 py-0.5 whitespace-nowrap"
       title={archivedView ? '복원' : '보관'}
      >{archivedView ? '복원' : '보관'}</button>
     </li>
    ))}
   </ul>

   {showCompose && (
    <ComposeMessageModal
     onClose={() => setShowCompose(false)}
     onSent={(cid) => {
      setShowCompose(false);
      navigate(`/inbox/${cid}`);
     }}
    />
   )}
  </div>
 );
};

export default Inbox;
